import { useState, useEffect } from "react";
import styles from "./NavBar.module.scss";
import { ReactComponent as SendIcon } from "../../assets/icons/sendIcon.svg";
import { ReactComponent as HomeIcon } from "../../assets/icons/homeIcon.svg";
import { ReactComponent as AboutIcon } from "../../assets/icons/aboutIcon.svg";
import { ReactComponent as SkillsIcon } from "../../assets/icons/skillsIcon.svg";
import { ReactComponent as WorksIcon } from "../../assets/icons/worksIcon.svg";
import { ReactComponent as ContactIcon } from "../../assets/icons/contactIcon.svg";
import useActiveSection from "../../hooks/useActiveSection";
import { useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { label: "Home", href: "#home", icon: "home" },
  { label: "About", href: "#about", icon: "about" },
  { label: "Skills", href: "#skills", icon: "skills" },
  { label: "Projects", href: "#projects", icon: "projects" },
  { label: "Contact", href: "#contact", icon: "contact" },
];

const LINK_ICONS = {
  home: HomeIcon,
  about: AboutIcon,
  skills: SkillsIcon,
  projects: WorksIcon,
  contact: ContactIcon,
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const active = useActiveSection(NAV_LINKS);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => {
      const scrollY = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      setScrolled(scrollY > 20);
      setProgress(docHeight > 0 ? (scrollY / docHeight) * 100 : 0);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // close drawer on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (!e.target.closest("#navbar") && !e.target.closest(".nb-drawer")) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuOpen]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate("/" + href);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <nav
        id="navbar"
        className={` ${styles.navbar} ${scrolled ? styles.scrolled : ""}`}
      >
        <div
          className={styles["nb-progress"]}
          style={{ width: `${progress}%` }}
        />

        <div className={styles["nb-inner"]}>
          <a
            href="#home"
            className={styles["nb-logo"]}
            onClick={(e) => handleNavClick(e, "#home")}
          >
            <div className={styles["nb-logo-mark"]}>S</div>
          </a>

          <ul className={styles["nb-links"]}>
            {NAV_LINKS.map(({ label, href }) => (
              <li
                key={href}
                className={`${styles["nb-link"]} ${active === href.slice(1) ? styles.active : ""}`}
              >
                <a href={href} onClick={(e) => handleNavClick(e, href)}>
                  {label}
                  <span className={styles["nb-link-bar"]} />
                </a>
              </li>
            ))}
          </ul>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <a
              href="#contact"
              className={styles["nb-cta"]}
              onClick={(e) => handleNavClick(e, "#contact")}
            >
              <span>Hire Me</span>
              <SendIcon />
            </a>
            <button
              className={`${styles["nb-hamburger"]} ${menuOpen ? styles.open : ""}`}
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </nav>

      {/* backdrop */}
      <div
        className={`${styles["nb-backdrop"]} ${menuOpen ? styles.open : ""}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* drawer panel */}
      <div className={`${styles["nb-drawer"]} ${menuOpen ? styles.open : ""}`}>
        {/* nav links */}
        <ul className={`${styles["nb-dl"]}`}>
          {NAV_LINKS.map(({ label, href, icon }) => {
            const Icon = LINK_ICONS[icon];
            const isActive = active === href.slice(1);
            return (
              <li key={href} className={isActive ? styles["nb-active"] : ""}>
                <a href={href} onClick={(e) => handleNavClick(e, href)}>
                  <span className={styles["nb-di"]}>
                    <Icon />
                  </span>
                  <span className={styles["nb-dll"]}>{label}</span>
                  <span className={styles["nb-dad"]} />
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}
